<template>
  <v-dialog
    :value="value"
    :content-class="contentClasses"
    fullscreen
    hide-overlay
    :show-back-button="showBackButton"
    eager
    @input="$emit('input', $event)"
  >
    <v-card
      v-resize="onResize"
      :class="`d-flex flex-column ${bodyOverflow ? 'justify-start' : 'justify-center'}`"
      :data-cy="dataCy"
      flat
    >
      <!-- Full Size Header on modal -->
      <div
        v-if="fullSizeHeaderOnMobile"
        class="d-flex flex-row pt-6 pb-6 sn-background-gray-01"
      >
        <div
          :class="{ hide: !showBackButton }"
          class="modal-title-button mr-auto mt-6 ml-6 flex-row d-flex"
          @click="back"
        >
          <v-icon
            data-cy="back-modal-btn"
            class="sn-body-1"
          >
            $snArrowLeft
          </v-icon>
          <span>Back</span>
        </div>
        <div>
          <h1
            class="sn-h1"
            :style="{ 'text-align': 'center' }"
          >
            {{ title }}
          </h1>
          <h2
            class="sn-body-1"
            :style="{ 'text-align': 'center' }"
          >
            {{ subTitle }}
          </h2>
        </div>
        <v-icon
          type=""
          class="modal-title-button ml-auto mt-6 mr-6"
          data-cy="exit-modal-btn"
          @click="close"
        >
          $snExit
        </v-icon>
      </div>
      <!-- Standard Modal -->
      <v-row
        v-else
        ref="xsTitle"
        :class="`sn-h3 flex-grow-0 px-4 ${bodyOverflow ? 'sn-background-gray-01 pt-6 pb-4' : 'pt-10'}`"
        data-cy="modal-title"
        no-gutters
      >
        {{ title }}
        <template v-if="type === 'dismiss'">
          <v-spacer />
          <v-icon
            type=""
            class="mr-4"
            data-cy="exit-modal-btn"
            @click="close"
          >
            $snExit
          </v-icon>
        </template>
      </v-row>
      <v-row
        ref="xsBody"
        class="sn-body-1 flex-grow-0 overflow-y-auto pa-4"
        :style="`max-height: ${xsMaxHeight}px`"
        no-gutters
      >
        <v-card-text
          class="pa-0"
          data-cy="modal-text"
        >
          <slot />
        </v-card-text>
      </v-row>
      <v-row
        v-if="type !== 'dismiss'"
        ref="xsActions"
        :class="`flex-grow-0 px-4 py-6 ${bodyOverflow ? 'sn-background-gray-01' : ''}`"
        justify="center"
        no-gutters
      >
        <v-row
          v-if="warningText"
          class="sn-text-error mb-4"
          no-gutters
        >
          <v-icon
            v-if="warningIcon"
            class="warning-icon mr-3"
          >
            {{ warningIcon }}
          </v-icon>
          {{ warningText }}
        </v-row>
        <sn-btn
          :disabled="disabled"
          :text="primaryText"
          :text-case="primaryTextCase"
          :loading="loading"
          :warning="warningButton"
          size="full-width"
          style="width: 100%;"
          data-cy="modal-confirmation-btn"
          :data-pendo="snInputProps_pendo + '-Add'"
          :prepend-icon="icon"
          @click="submit"
        />
        <sn-btn
          v-if="type === 'decision'"
          :text="secondaryText"
          class="mt-4"
          type="secondary"
          size="full-width"
          style="width: 100%;"
          data-cy="modal-cancellation-btn"
          :data-pendo="snInputProps_pendo + '-Cancel'"
          @click="close"
        />
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import SnBtn from '../snBtn/snBtn.vue'
import SnInputProps from '../props/SnInputProps'
import { cypressMixin } from '../mixins/cypressMixin'

export default {
  name: 'MobileModal',
  components: {
    SnBtn
  },
  mixins: [cypressMixin],
  props: {
    ...SnInputProps,
    value: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      default: 'confirmation',
      validator (type) {
        return ['confirmation', 'decision', 'dismiss'].includes(type)
      }
    },
    title: {
      type: String,
      default: 'Modal'
    },
    subTitle: {
      type: String,
      default: ''
    },
    primaryText: {
      type: String,
      default: 'Okay'
    },
    primaryTextCase: {
      type: String,
      default: 'sentence',
      required: false,
      validator (primaryTextCase) {
        return ['uppercase', 'title', 'sentence', ''].includes(primaryTextCase)
      }
    },
    secondaryText: {
      type: String,
      default: 'Cancel'
    },
    warningText: {
      type: String,
      default: ''
    },
    warningIcon: {
      type: String,
      default: ''
    },
    warningButton: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ''
    },
    contentClass: {
      type: String,
      default: ''
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    fullSizeHeaderOnMobile: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      bodyOverflow: false,
      xsMaxHeight: 0,
      windowSize: {
        x: 0,
        y: 0
      }
    }
  },

  computed: {
    contentClasses () {
      const classes = ['sn-mobile-modal']
      if (this.modalClass) {
        classes.push(this.contentClass)
      }
      return classes.join(' ')
    }
  },

  watch: {
    value: function (val) {
      if (val) {
        this.getOverflow()
      }
    },
    warningText: function () {
      this.getOverflow()
    }
  },

  mounted () {
    this.onResize()
  },

  methods: {
    close () {
      this.$emit('close')
    },

    submit () {
      this.$emit('submit')
    },

    getOverflow () {
      this.$nextTick(() => {
        this.$nextTick(() => {
          const height = this.windowSize.y - this.$refs?.xsTitle?.clientHeight
          if (this.type !== 'dismiss') {
            this.xsMaxHeight = height - this.$refs?.xsActions?.clientHeight
            const body = this.$refs?.xsBody
            this.bodyOverflow = body?.clientHeight < body?.scrollHeight
          } else {
            this.xsMaxHeight = height
            this.bodyOverflow = true
          }
        })
      })
    },

    onResize () {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
      this.getOverflow()
    },
    back () {
      this.$emit('back')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

::v-deep {
  z-index: 1000;
  margin: 0px !important;
  max-height: 100% !important;
  border-radius: 0px;
  max-width: 100% !important;
  width: 100% !important;

  .v-card {
    max-height: 100% !important;
    height: 100vh;
    min-width: 100% !important;

    .sn-h3 {
      width: 100%;

      .v-icon {
        -webkit-appearance: none;
        color: $sn-primary-03;
      }
    }

    .v-icon.warning-icon {
      color: $sn-error;
    }

    .v-card__text {
      width: 100%;
    }
  }
}

.modal-title-button {
  width: 64px !important;
}

.hide {
  visibility: hidden;
}

</style>
